import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiUsers, FiPhone, FiMail, FiShoppingCart, FiBookOpen, 
  FiHeadphones, FiTool, FiStar, FiSearch, FiFilter
} from 'react-icons/fi';
import { BiBot } from 'react-icons/bi';
import { AssistantConfig } from '../types/assistant.types';
import { theme } from '../styles/theme';

export interface AssistantTemplate {
  id: string;
  name: string;
  description: string;
  icon: React.ReactNode;
  category: TemplateCategory;
  tags: string[];
  config: Partial<AssistantConfig>;
  featured?: boolean;
  usageCount?: number;
}

export type TemplateCategory = 
  | 'customer-service' 
  | 'sales' 
  | 'support' 
  | 'education' 
  | 'healthcare' 
  | 'general'
  | 'custom';

interface FormTemplateProps {
  onSelectTemplate: (template: AssistantTemplate) => void;
  selectedTemplateId?: string;
  className?: string;
}

const FormTemplate: React.FC<FormTemplateProps> = ({
  onSelectTemplate,
  selectedTemplateId,
  className = ''
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<TemplateCategory | 'all'>('all');
  const [showFeaturedOnly, setShowFeaturedOnly] = useState(false);

  const templates: AssistantTemplate[] = [
    {
      id: 'customer-service-bot',
      name: 'Customer Service Assistant',
      description: 'Handle customer inquiries, complaints, and provide support across multiple channels.',
      icon: <FiHeadphones className="text-blue-500" />,
      category: 'customer-service',
      tags: ['support', 'helpdesk', 'multichannel'],
      featured: true,
      usageCount: 1250,
      config: {
        name: 'Customer Service Assistant',
        description: 'AI assistant specialized in customer service and support',
        model: 'gpt-5',
        temperature: 0.3,
        maxTokens: 1500,
        systemPrompt: `You are a professional customer service assistant. Your role is to:
- Provide helpful and accurate information
- Handle customer complaints with empathy
- Escalate complex issues to human agents when needed
- Maintain a friendly and professional tone
- Follow company policies and procedures

Always aim to resolve issues efficiently while ensuring customer satisfaction.`,
        voiceEnabled: true,
        tools: [
          { id: 'lookup_contact', name: 'Lookup Contact', description: 'Look up customer contact information', category: 'crm', enabled: true, configuration: {} },
          { id: 'create_ticket', name: 'Create Ticket', description: 'Create a support ticket', category: 'workflow_automation', enabled: true, configuration: {} },
          { id: 'transfer_to_agent', name: 'Transfer to Agent', description: 'Transfer the conversation to a human agent', category: 'communication', enabled: true, configuration: {} }
        ]
      }
    },
    {
      id: 'sales-assistant',
      name: 'Sales Assistant',
      description: 'Generate leads, qualify prospects, and guide customers through the sales process.',
      icon: <FiShoppingCart className="text-green-500" />,
      category: 'sales',
      tags: ['leads', 'conversion', 'crm'],
      featured: true,
      usageCount: 890,
      config: {
        name: 'Sales Assistant',
        description: 'AI assistant focused on sales and lead generation',
        model: 'claude-opus-4-1',
        temperature: 0.4,
        maxTokens: 1200,
        systemPrompt: `You are a professional sales assistant. Your responsibilities include:
- Qualifying leads and understanding customer needs
- Presenting product benefits and features
- Handling objections professionally
- Guiding customers through the sales process
- Creating quotes and proposals
- Following up on opportunities

Focus on building relationships and providing value to potential customers.`,
        tools: [
          { id: 'create_lead', name: 'Create Lead', description: 'Create a new sales lead', category: 'workflow_automation', enabled: true, configuration: {} },
          { id: 'update_crm', name: 'Update CRM', description: 'Update customer relationship management system', category: 'workflow_automation', enabled: true, configuration: {} },
          { id: 'generate_report', name: 'Generate Report', description: 'Generate a sales report', category: 'communication', enabled: true, configuration: {} }
        ]
      }
    },
    {
      id: 'appointment-scheduler',
      name: 'Appointment Scheduler',
      description: 'Schedule, reschedule, and manage appointments with calendar integration.',
      icon: <FiPhone className="text-purple-500" />,
      category: 'support',
      tags: ['scheduling', 'calendar', 'appointments'],
      featured: false,
      usageCount: 670,
      config: {
        name: 'Appointment Scheduler',
        description: 'AI assistant for appointment scheduling and management',
        model: 'gpt-3.5-turbo',
        temperature: 0.2,
        maxTokens: 1000,
        systemPrompt: `You are an appointment scheduling assistant. Your main tasks are:
- Checking availability and suggesting appointment slots
- Booking, rescheduling, and canceling appointments
- Sending confirmation and reminder messages
- Handling scheduling conflicts professionally
- Collecting necessary information from customers

Always confirm appointment details and send appropriate notifications.`,
        voiceEnabled: true,
        tools: [
          { id: 'check_availability', name: 'Check Availability', description: 'Check calendar availability', category: 'calendar', enabled: true, configuration: {} },
          { id: 'book_appointment', name: 'Book Appointment', description: 'Book an appointment in calendar', category: 'calendar', enabled: true, configuration: {} },
          { id: 'reschedule', name: 'Reschedule', description: 'Reschedule an existing appointment', category: 'calendar', enabled: true, configuration: {} },
          { id: 'send_sms', name: 'Send SMS', description: 'Send SMS message to client', category: 'communication', enabled: true, configuration: {} }
        ]
      }
    },
    {
      id: 'educational-tutor',
      name: 'Educational Tutor',
      description: 'Provide educational support, answer questions, and guide learning processes.',
      icon: <FiBookOpen className="text-orange-500" />,
      category: 'education',
      tags: ['tutoring', 'learning', 'education'],
      featured: false,
      usageCount: 520,
      config: {
        name: 'Educational Tutor',
        description: 'AI tutor for educational support and learning guidance',
        model: 'gemini-2.5-pro',
        temperature: 0.5,
        maxTokens: 2000,
        systemPrompt: `You are an educational tutor assistant. Your role involves:
- Explaining complex concepts in simple terms
- Providing step-by-step guidance
- Encouraging student engagement and curiosity
- Adapting to different learning styles
- Offering practice exercises and examples
- Tracking learning progress

Always be patient, encouraging, and focused on helping students understand rather than just providing answers.`,
        tools: [
          { id: 'search_web', name: 'Search The Web', description: 'Search the web for information', category: 'data_analysis', enabled: true, configuration: {} },
          { id: 'create_note', name: 'Create Note', description: 'Create a note or memo', category: 'communication', enabled: true, configuration: {} },
          { id: 'generate_report', name: 'Generate Report', description: 'Generate a research report', category: 'communication', enabled: true, configuration: {} }
        ]
      }
    },
    {
      id: 'technical-support',
      name: 'Technical Support',
      description: 'Troubleshoot technical issues and provide step-by-step solutions.',
      icon: <FiTool className="text-red-500" />,
      category: 'support',
      tags: ['technical', 'troubleshooting', 'IT'],
      featured: false,
      usageCount: 340,
      config: {
        name: 'Technical Support Assistant',
        description: 'AI assistant for technical support and troubleshooting',
        model: 'gpt-5',
        temperature: 0.1,
        maxTokens: 1800,
        systemPrompt: `You are a technical support assistant. Your expertise includes:
- Diagnosing technical problems systematically
- Providing clear, step-by-step solutions
- Explaining technical concepts in user-friendly terms
- Escalating complex issues when necessary
- Creating documentation for common issues
- Following troubleshooting best practices

Always gather sufficient information before providing solutions and ensure users understand each step.`,
        tools: [
          { id: 'create_ticket', name: 'Create Ticket', description: 'Create a support ticket', category: 'communication', enabled: true, configuration: {} },
          { id: 'search_web', name: 'Search The Web', description: 'Search the web for information', category: 'data_analysis', enabled: true, configuration: {} },
          { id: 'transfer_to_agent', name: 'Transfer to Agent', description: 'Transfer to human agent', category: 'communication', enabled: true, configuration: {} }
        ]
      }
    },
    {
      id: 'general-assistant',
      name: 'General Purpose Assistant',
      description: 'Versatile assistant for various tasks and general inquiries.',
      icon: <BiBot className="text-gray-500" />,
      category: 'general',
      tags: ['versatile', 'general', 'multipurpose'],
      featured: false,
      usageCount: 1100,
      config: {
        name: 'General Assistant',
        description: 'Versatile AI assistant for various tasks',
        model: 'gpt-3.5-turbo',
        temperature: 0.7,
        maxTokens: 1000,
        systemPrompt: `You are a helpful general-purpose assistant. You can help with:
- Answering questions on various topics
- Providing information and explanations
- Assisting with basic tasks
- Offering suggestions and recommendations
- Having natural conversations

Always be helpful, accurate, and considerate in your responses.`,
        tools: [
          { id: 'search_web', name: 'Search The Web', description: 'Search the web for information', category: 'data_analysis', enabled: true, configuration: {} },
          { id: 'get_weather', name: 'Get Weather', description: 'Get weather information', category: 'data_analysis', enabled: true, configuration: {} }
        ]
      }
    }
  ];

  const categories: { id: TemplateCategory | 'all'; name: string; icon: React.ReactNode }[] = [
    { id: 'all', name: 'All Templates', icon: <FiFilter /> },
    { id: 'customer-service', name: 'Customer Service', icon: <FiHeadphones /> },
    { id: 'sales', name: 'Sales', icon: <FiShoppingCart /> },
    { id: 'support', name: 'Support', icon: <FiTool /> },
    { id: 'education', name: 'Education', icon: <FiBookOpen /> },
    { id: 'general', name: 'General', icon: <BiBot /> }
  ];

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         template.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || template.category === selectedCategory;
    const matchesFeatured = !showFeaturedOnly || template.featured;
    
    return matchesSearch && matchesCategory && matchesFeatured;
  });

  return (
    <div className={`space-y-6 ${className}`}>
      {/* Header */}
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2" style={{ color: theme.colors.text.primary }}>Choose a Template</h3>
        <p className="text-sm" style={{ color: theme.colors.text.secondary }}>
          Start with a pre-configured assistant template or create your own from scratch
        </p>
      </div>

      {/* Search and Filters */}
      <div className="space-y-4">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2" style={{ color: theme.colors.text.tertiary }} />
          <input
            type="text"
            placeholder="Search templates..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:outline-none"
            style={{
              backgroundColor: theme.colors.background.secondary,
              borderColor: theme.colors.border.main,
              color: theme.colors.text.primary
            }}
          />
        </div>

        <div className="flex flex-wrap gap-2">
          {categories.map(category => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-colors"
              style={{
                backgroundColor: selectedCategory === category.id
                  ? theme.colors.primary.main + '20' // 20% opacity
                  : theme.colors.background.tertiary,
                color: selectedCategory === category.id
                  ? theme.colors.primary.light
                  : theme.colors.text.secondary,
                border: selectedCategory === category.id
                  ? `1px solid ${theme.colors.primary.main}`
                  : `1px solid ${theme.colors.border.light}`
              }}
            >
              {category.icon}
              {category.name}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-sm" style={{ color: theme.colors.text.secondary }}>
            <input
              type="checkbox"
              checked={showFeaturedOnly}
              onChange={(e) => setShowFeaturedOnly(e.target.checked)}
              className="rounded focus:ring-2"
              style={{
                accentColor: theme.colors.primary.main,
                borderColor: theme.colors.border.main
              }}
            />
            Show featured templates only
          </label>
          <span className="text-xs" style={{ color: theme.colors.text.tertiary }}>
            {filteredTemplates.length} template{filteredTemplates.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Templates Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-96 overflow-y-auto">
        <AnimatePresence>
          {filteredTemplates.map(template => (
            <motion.div
              key={template.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="relative p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md"
              style={{
                borderColor: selectedTemplateId === template.id
                  ? theme.colors.primary.main
                  : theme.colors.border.main,
                backgroundColor: selectedTemplateId === template.id
                  ? theme.colors.primary.main + '10' // 10% opacity
                  : theme.colors.background.card
              }}
              onClick={() => onSelectTemplate(template)}
            >
              {template.featured && (
                <div className="absolute top-2 right-2">
                  <FiStar className="text-yellow-500 fill-current" size={16} />
                </div>
              )}
              
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 p-2 rounded-lg" style={{ backgroundColor: theme.colors.background.tertiary }}>
                  {template.icon}
                </div>
                
                <div className="flex-1 min-w-0">
                  <h4 className="font-medium truncate" style={{ color: theme.colors.text.primary }}>
                    {template.name}
                  </h4>
                  <p className="text-sm mt-1 line-clamp-2" style={{ color: theme.colors.text.secondary }}>
                    {template.description}
                  </p>
                  
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex flex-wrap gap-1">
                      {template.tags.slice(0, 2).map(tag => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 text-xs rounded"
                          style={{
                            backgroundColor: theme.colors.background.tertiary,
                            color: theme.colors.text.secondary
                          }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                    
                    {template.usageCount && (
                      <span className="text-xs ml-auto" style={{ color: theme.colors.text.tertiary }}>
                        <FiUsers className="inline mr-1" size={12} />
                        {template.usageCount.toLocaleString()} uses
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-8">
          <BiBot className="mx-auto mb-4" style={{ color: theme.colors.text.tertiary }} size={48} />
          <p className="mb-2" style={{ color: theme.colors.text.secondary }}>No templates found</p>
          <p className="text-sm" style={{ color: theme.colors.text.tertiary }}>
            Try adjusting your search or filter criteria
          </p>
        </div>
      )}
    </div>
  );
};

export default FormTemplate;